import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { StudentCourseEnrolmentEventType } from '@duke-hq/svc-api-db';
import { StudentCourseEnrolmentEventTypeSchema } from '@duke-hq/svc-api-db/schemas/enums/StudentCourseEnrolmentEventType.schema';

import { JsonViewer } from '@duke-hq/ui/components/JsonViewer/JsonViewer';
import PdfGeneratorButton, {
  isDocumentMetadata,
} from '@duke-hq/ui/components/PdfGeneratorButton/index';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { djs } from '@duke-hq/djs';
import {
  ArrowRightFromLine,
  ChevronDown,
  ExternalLink,
  FileDown,
  RefreshCw,
  X,
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { jsonToCSV, useCSVDownloader } from 'react-papaparse';
import { type NavigateOptions, useSearchParams } from 'react-router-dom';
import EventChip from './EventChip';
import { withPermission } from '../withPermission';

type StudentCourseEnrolmentEvent =
  RouterOutputs['courseEnrolmentEvents']['getAll']['events'][number];

// Event card component
const EventCard = memo(
  ({ event }: { event: StudentCourseEnrolmentEvent }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
      <Box
        p={4}
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        bg="bg.canvas"
        transition="all 0.2s"
        _hover={{ shadow: 'sm', borderColor: 'border.emphasis' }}
      >
        <Flex justify="space-between" align="flex-start" mb={2}>
          <HStack>
            <EventChip eventType={event.courseEnrolmentEventType} />
            {event.courseEnrolmentEventType === 'DOCUMENT_GENERATED' &&
              isDocumentMetadata(event.metadata) && (
                <PdfGeneratorButton
                  documentType={event.metadata.documentType}
                  data={event.metadata.documentData}
                  label="View"
                  colorScheme="blue"
                  size="xs"
                  rightIcon={<ExternalLink size="14" />}
                />
              )}

            <Text fontWeight="medium" fontSize="sm" color="fg.subtle">
              <Trans>Course enrolment id:</Trans> {event.courseEnrolmentId.toString()}
            </Text>
          </HStack>
          <Text fontSize="xs" color="fg.subtle">
            {djs(event.eventOccurredAt).format('MMM D, YYYY h:mm A')}
          </Text>
        </Flex>

        <Text fontWeight="medium" mb={1}>
          {event.description}
        </Text>

        {event.metadata && Object.keys(event.metadata).length > 0 ? (
          <Box mt={2} pt={2} borderTopWidth="1px" borderColor="gray.100">
            <Button variant="link" fontSize="xs" mb={2} onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <Trans>Hide Metadata</Trans> : <Trans>Show Metadata</Trans>}
            </Button>
            {isExpanded === true && (
              <JsonViewer
                data={event.metadata}
                title={<Trans>Metadata</Trans>}
                initialExpandDepth={1}
              />
            )}
          </Box>
        ) : null}
      </Box>
    );
  },
  (prevProps, nextProps) => prevProps.event.id === nextProps.event.id,
);
EventCard.displayName = 'EventCard';

// Empty state component
function EmptyState(): React.ReactElement {
  return (
    <Box
      p={8}
      textAlign="center"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.default"
      bg="bg.canvas"
    >
      <Text fontSize="lg" color="fg.subtle">
        <Trans>No events found</Trans>
      </Text>
      <Text fontSize="sm" color="fg.disabled" mt={2}>
        <Trans>Try adjusting your filters or check back later</Trans>
      </Text>
    </Box>
  );
}

// Loading skeleton component
function LoadingSkeleton(): React.ReactElement {
  return (
    <VStack spacing={3} align="stretch" width="full">
      {Array.from({ length: 3 }).map((_, index) => (
        <Box
          key={`skeleton-${index}`}
          p={4}
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          aria-label={t`Loading event`}
          width="full"
        >
          <Flex justify="space-between" align="flex-start" mb={2}>
            <HStack>
              <Skeleton height="20px" width="80px" />
              <Skeleton height="16px" width="120px" />
            </HStack>
            <Skeleton height="14px" width="100px" />
          </Flex>
          <Skeleton height="20px" width="90%" my={2} />
          <Skeleton height="60px" width="100%" mt={4} />
        </Box>
      ))}
    </VStack>
  );
}

const eventTypeOptions = Object.values(StudentCourseEnrolmentEventType).map((type) => ({
  id: type,
  value: type,
  label: type.replace(/_/g, ' '), // Format label for display
}));

const CourseEnrolmentEventLogs = withPermission('canViewFinances', () => {
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '0px 0px 300px 0px', // Load more when 300px from the bottom
  });
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { CSVDownloader } = useCSVDownloader();

  // Held steady for the lifetime of the page. These are the fallback used when the
  // URL carries no date range, and they feed the query args, so building a fresh
  // `new Date()` on each render would hand the query a new date range — and so a new
  // query key — every render, refetching in a loop.
  const [defaultStartDate, defaultEndDate] = useMemo<[Date, Date]>(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 1);

    return [start, end];
  }, []);

  const [selectedDates, setSelectedDates] = useState([defaultStartDate, defaultEndDate]);

  const [searchParams, setSearchParams] = useSearchParams({
    courseEnrolmentEventType: [],
    startDate: defaultStartDate.toISOString(),
    endDate: defaultEndDate.toISOString(),
  });

  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  // SearchInput holds its own text, so clearing remounts it to empty the box.
  const [searchInputKey, setSearchInputKey] = useState(0);

  // The router applies URL changes as transitions, so `searchParams` can lag behind a
  // write that has not committed yet. Each filter change builds on this ref instead,
  // so a quick second change adds to the first rather than overwriting it.
  const latestSearchParamsRef = useRef(searchParams);
  useEffect(() => {
    latestSearchParamsRef.current = searchParams;
  }, [searchParams]);

  const writeSearchParams = useCallback(
    (nextParams: URLSearchParams, options?: NavigateOptions) => {
      latestSearchParamsRef.current = nextParams;
      setSearchParams(nextParams, options);
    },
    [setSearchParams],
  );

  const handleSearchCommit = useCallback(
    (value: string) => {
      setSearch(value);

      const nextParams = new URLSearchParams(latestSearchParamsRef.current);
      if (value.length > 0) {
        nextParams.set('search', value);
      } else {
        nextParams.delete('search');
      }
      writeSearchParams(nextParams, { replace: true });
    },
    [writeSearchParams],
  );

  const writeDateRange = useCallback(
    (startDate: Date, endDate: Date) => {
      const nextParams = new URLSearchParams(latestSearchParamsRef.current);
      nextParams.set('startDate', startDate.toISOString());
      nextParams.set('endDate', endDate.toISOString());
      writeSearchParams(nextParams);
    },
    [writeSearchParams],
  );

  const selectedEventTypes = searchParams.get('courseEnrolmentEventType')?.split(',') || [];

  const dateRange = useMemo(() => {
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const startDate = startDateParam != null ? new Date(startDateParam) : defaultStartDate;
    const endDate = endDateParam != null ? new Date(endDateParam) : defaultEndDate;

    return [
      isNaN(startDate.getTime()) ? defaultStartDate : startDate,
      isNaN(endDate.getTime()) ? defaultEndDate : endDate,
    ];
  }, [searchParams, defaultStartDate, defaultEndDate]);

  const courseEnrolmentEventsGetAllArgs = useMemo(
    () => ({
      startDate: dateRange[0],
      endDate: dateRange[1],
      courseEnrolmentId: search.length ? parseInt(search.trim()) : undefined,
      courseEnrolmentEventType: searchParams
        .get('courseEnrolmentEventType')
        ?.split(',')
        .map((type) => StudentCourseEnrolmentEventTypeSchema.safeParse(type).data)
        .filter((type) => type !== undefined),
    }),
    [search, dateRange, searchParams],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch, isError } =
    trpcAdmin.courseEnrolmentEvents.getAll.useInfiniteQuery(courseEnrolmentEventsGetAllArgs, {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    });

  const events = data?.pages.flatMap((page) => page.events) || [];

  const exportData = trpcAdmin.courseEnrolmentEvents.exportData.useMutation();

  // Load more when the sentinel comes into view.
  //
  // cancelRefetch defaults to true, which makes a second call abort the request already
  // in flight and start another. The isFetchingNextPage guard below only closes that
  // window once React has committed the fetching state, and scrolling can flip the
  // sentinel in and out before then. Opting out makes the extra calls reuse the
  // in-flight request instead, so a page is fetched once however often this fires.
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const setDateRangeFromPast = useCallback(
    (days: number) => {
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - days);
      setSelectedDates([start, end]);
      writeDateRange(start, end);
    },
    [writeDateRange],
  );

  const handleDateRangeChange = useCallback(
    (dates: Date[]) => {
      setSelectedDates(dates);

      // The picker reports the first click of a new range as a single date. Show it
      // in the field so the user can pick the other end, but leave the filter alone
      // until the range is complete — clearing the dates here would drop the query
      // back to the default range and fire a request mid-selection.
      const [rangeStart, rangeEnd] = dates;
      if (rangeStart == null || rangeEnd == null) {
        return;
      }

      // Set start date to the very beginning of the first day
      const startDate = new Date(rangeStart);
      startDate.setHours(0, 0, 0, 0);

      // Set end date to the very end of the last day
      const endDate = new Date(rangeEnd);
      endDate.setHours(23, 59, 59, 999);

      writeDateRange(startDate, endDate);
    },
    [writeDateRange],
  );

  const handleClearFilters = useCallback(() => {
    // Fresh dates rather than the mount-time defaults, so clearing always means the
    // last 24 hours as of now however long the page has been open.
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 1);

    writeSearchParams(
      new URLSearchParams({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      }),
    );
    setSearch('');
    setSearchInputKey((key) => key + 1);
    setSelectedDates([start, end]);
  }, [writeSearchParams]);

  const handleEventTypeChange = useCallback(
    (type: StudentCourseEnrolmentEventType) => {
      const newParams = new URLSearchParams(latestSearchParamsRef.current);
      const current = newParams.get('courseEnrolmentEventType');
      const currentTypes = current != null ? current.split(',') : [];

      if (currentTypes.includes(type)) {
        // Remove the type
        const updated = currentTypes.filter((t) => t !== type);
        if (updated.length > 0) {
          newParams.set('courseEnrolmentEventType', updated.join(','));
        } else {
          newParams.delete('courseEnrolmentEventType');
        }
      } else {
        // Add the type
        currentTypes.push(type);
        newParams.set('courseEnrolmentEventType', currentTypes.join(','));
      }

      writeSearchParams(newParams);
    },
    [writeSearchParams],
  );

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded && (
          <IconButton
            aria-label="expand secondary sidebar"
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ),
        <PageHeading key="page-heading">
          <Trans>Course Enrolment Events</Trans>
        </PageHeading>,
      ]}
      filters={[
        <InputGroup key="search" maxW="md">
          <SearchInput
            key={searchInputKey}
            urlSync={false}
            defaultValue={search}
            placeholder={t`Course enrolment id`}
            onCommit={handleSearchCommit}
          />
        </InputGroup>,
        <ButtonGroup key="date-range" isAttached size="sm" variant="outline">
          <RangeDatepicker
            selectedDates={selectedDates}
            onDateChange={handleDateRangeChange}
            maxDate={defaultEndDate}
            configs={{
              dateFormat: 'dd/MM/yyyy',
            }}
            propsConfigs={{
              // A clicked month button that becomes disabled would take focus out of the
              // popover and close it, so clicks leave focus where it is.
              dateNavBtnProps: { onMouseDown: (event) => event.preventDefault() },
            }}
          />
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label={t`Date range select options`}
              icon={<Icon as={ChevronDown} boxSize={4} />}
              borderLeftRadius={0}
            />
            <MenuList>
              <MenuItem onClick={() => setDateRangeFromPast(1)}>
                <Trans>Last 24 hours</Trans>
              </MenuItem>
              <MenuItem onClick={() => setDateRangeFromPast(3)}>
                <Trans>Last 3 days</Trans>
              </MenuItem>
              <MenuItem onClick={() => setDateRangeFromPast(7)}>
                <Trans>Last 7 days</Trans>
              </MenuItem>
              <MenuItem onClick={() => setDateRangeFromPast(30)}>
                <Trans>Last 30 days</Trans>
              </MenuItem>
            </MenuList>
          </Menu>
        </ButtonGroup>,
      ]}
      tableActionsLeft={[
        <Box mb={4} key="event-types">
          <FormLabel>
            <Trans>Event Types</Trans>
          </FormLabel>
          <Wrap spacing={2}>
            {eventTypeOptions.map((option) => (
              <WrapItem key={option.value}>
                <Button
                  size="xs"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontWeight="medium"
                  transition="colors 0.2s"
                  onClick={() =>
                    handleEventTypeChange(option.value as StudentCourseEnrolmentEventType)
                  }
                  variant={selectedEventTypes.includes(option.value) ? 'solid' : 'outline'}
                  colorScheme={selectedEventTypes.includes(option.value) ? 'brand' : 'gray'}
                  bg={selectedEventTypes.includes(option.value) ? 'brand.100' : 'gray.100'}
                  color={selectedEventTypes.includes(option.value) ? 'brand.800' : 'gray.800'}
                  _hover={{
                    bg: selectedEventTypes.includes(option.value) ? 'brand.200' : 'gray.200',
                  }}
                >
                  {option.label}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
          {selectedEventTypes.length > 0 && (
            <Text mt={2} fontSize="xs" color="brand.600">
              {selectedEventTypes.length} event type
              {selectedEventTypes.length > 1 ? 's' : ''} selected
            </Text>
          )}
        </Box>,
      ]}
      tableActionsRight={[
        <Tooltip key="clear-filters" label={t`Clear filters`}>
          <IconButton
            aria-label={t`Clear filters`}
            icon={<X />}
            onClick={handleClearFilters}
            isDisabled={isFetchingNextPage || isLoading}
            size="xs"
            variant="outline"
          />
        </Tooltip>,
        <Tooltip key="refresh" label={t`Refresh data`}>
          <IconButton
            aria-label={t`Refresh data`}
            icon={<RefreshCw />}
            onClick={() => void refetch()}
            isDisabled={isFetchingNextPage || isLoading}
            size="xs"
            variant="outline"
          />
        </Tooltip>,
        <CSVDownloader
          key="export"
          type="link"
          filename={`course_enrolment_events_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
          data={async () =>
            jsonToCSV((await exportData.mutateAsync(courseEnrolmentEventsGetAllArgs)) ?? [])
          }
        >
          <Tooltip key="export" label={t`Export data`}>
            <IconButton
              aria-label={t`Export data`}
              icon={<FileDown />}
              isDisabled={isFetchingNextPage || isLoading}
              size="xs"
            />
          </Tooltip>
        </CSVDownloader>,
      ]}
      table={
        <Box
          bg="bg.surface"
          boxShadow={{ base: 'none', md: 'sm' }}
          borderRadius={{ base: 'none', md: 'lg' }}
        >
          <Stack spacing="5">
            <Box overflowX="auto" p={4}>
              {isError ? (
                <Box
                  p={8}
                  textAlign="center"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="red.100"
                  bg="red.50"
                >
                  <Text fontSize="lg" color="red.600">
                    <Trans>Error loading events</Trans>
                  </Text>
                  <Button mt={4} size="sm" colorScheme="red" onClick={() => void refetch()}>
                    <Trans>Try Again</Trans>
                  </Button>
                </Box>
              ) : isLoading && events.length === 0 ? (
                <LoadingSkeleton />
              ) : events.length === 0 ? (
                <EmptyState />
              ) : (
                <VStack spacing={3} align="stretch">
                  {events.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}

                  {/* Sentinel element for infinite scrolling */}
                  <Box ref={ref} h="10px" />

                  {isFetchingNextPage ? (
                    <Flex justify="center" width="full" py={4}>
                      <LoadingSkeleton />
                    </Flex>
                  ) : null}

                  {!hasNextPage && events.length > 0 && (
                    <Box textAlign="center" py={4}>
                      <Divider mb={4} />
                      <Text color="fg.subtle" fontSize="sm">
                        <Trans>End of events</Trans>
                      </Text>
                    </Box>
                  )}
                </VStack>
              )}
            </Box>
          </Stack>
        </Box>
      }
    />
  );
});

export default CourseEnrolmentEventLogs;
